import {
  consoleTransport,
  LogLevel,
  type Logger,
  type LogRing,
  type RingLine,
} from '@littlebird/logger';

import type { SqliteExecutor } from '@littlebird/local-db';
import type {
  LittlebirdSDK,
  NdjsonBootstrapLine,
  PacketSummary,
  SyncStatus,
  TransactionSummary,
} from '@littlebird/sdk';

export const SYNC_VERBOSE_KEY = 'lb:sync-verbose';

export interface SyncConsoleDeps {
  getSdk: () => LittlebirdSDK | undefined;
  executor: SqliteExecutor;
  logger: Logger;
  consoleAttached: boolean;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  ring: Pick<LogRing, 'lines'>;
}

export type SyncConsole = ReturnType<typeof createSyncConsole>;

const DIFF_TABLES = [
  'threads',
  'meetings',
  'files',
  'journals',
  'projects',
] as const;
type DiffTable = (typeof DIFF_TABLES)[number];
const DIFF_ID_CAP = 100;

type ServerRow = { id: string; deleted_at?: string | null };
type BootstrapPage = {
  cursors: Record<string, unknown>;
  has_more: Record<string, boolean>;
};
type ServerLine =
  | { type: '_page'; data: BootstrapPage }
  | { type: string; data: ServerRow[] };

async function serverIds(
  sdk: LittlebirdSDK,
  table: DiffTable
): Promise<Set<string>> {
  const ids = new Set<string>();
  const lineTypes =
    table === 'threads' ? ['threads', 'routine_threads'] : [table];
  let cursors: Record<string, unknown> | undefined;
  for (;;) {
    const params = new URLSearchParams({
      only_models: table,
      chat_scopes: 'routine',
    });
    if (cursors) params.set('cursor', JSON.stringify(cursors));
    const supports = sdk.stores.supportedProducerKeys();
    if (supports.length > 0) params.set('supports', supports.join(','));

    let page: BootstrapPage | undefined;
    await sdk.apiClient.fetchNdjson<ServerLine>(`bootstrap/page?${params}`, {
      onLine: (line) => {
        if (line.type === '_page') page = line.data as BootstrapPage;
        else if (lineTypes.includes(line.type))
          for (const row of line.data as ServerRow[])
            if (!row.deleted_at) ids.add(row.id);
      },
    });

    if (!page || !Object.values(page.has_more).some(Boolean)) return ids;
    cursors = page.cursors;
  }
}

const HELP = `__sync.status()        one snapshot: phase, lastSyncId, queue, degraded
__sync.recentLogs()    last 300 sdk log lines (warn+ always; debug+ while verbose)
__sync.recentPackets() recent packet headers, including failures
__sync.queue()         pending / executing / awaiting-sync transactions (no payloads)
__sync.meta()          raw _meta row
__sync.syncActions(n)  newest n rows of _syncActions (default 20)
__sync.flush()         push pending transactions now
__sync.syncNow()       run an incremental bootstrap now
__sync.capture(true)   start raw bootstrap stream capture
__sync.capture(false)  stop capture and return lines
__sync.exportCapture() download captured lines as sync-capture.ndjson
__sync.replay(lines)   replay captured lines without network access
__sync.diff(table)     compare local ids against bootstrap/page
__sync.verbose(true)   debug-level sync logs to the console, persisted across reloads
__localDb.sql(text)    raw SQL against the local database`;

export function createSyncConsole(deps: SyncConsoleDeps) {
  const storage = deps.storage ?? safeLocalStorage();
  const originalLevel = deps.logger.level;
  let detachConsole: (() => void) | undefined;
  const sdk = () => {
    const instance = deps.getSdk();
    if (!instance) throw new Error('__sync: SDK not created yet');
    return instance;
  };
  const rows = async (sql: string, params: unknown[] = []) =>
    (
      await deps.executor.execute({
        sql,
        params: params as never[],
        method: 'all',
      })
    ).rows as unknown[][];

  return {
    status: (): Promise<SyncStatus> => sdk().getSyncStatus(),
    recentLogs: (): RingLine[] => deps.ring.lines(),
    recentPackets: (): PacketSummary[] => sdk().syncProcessor.recentPackets(),
    queue: (): TransactionSummary[] => sdk().transactionQueue.snapshot(),
    meta: async (): Promise<unknown> => {
      const [[, data] = []] = await rows(
        "SELECT id, data FROM _meta WHERE id = 'meta'"
      );
      return typeof data === 'string' ? JSON.parse(data) : data;
    },
    syncActions: (limit = 20): Promise<unknown[]> =>
      rows(
        'SELECT id, sync_id FROM _sync_actions ORDER BY sync_id DESC LIMIT ?',
        [limit]
      ),
    flush: (): void => sdk().transactionQueue.flush(),
    syncNow: (): Promise<void> => sdk().syncNow(),
    capture: (on: boolean): NdjsonBootstrapLine[] => {
      if (on) {
        sdk().startStreamCapture();
        return [];
      }
      return sdk().stopStreamCapture();
    },
    exportCapture: (): number => {
      const lines = sdk().stopStreamCapture();
      const url = URL.createObjectURL(
        new Blob([lines.map((line) => JSON.stringify(line)).join('\n')], {
          type: 'application/x-ndjson',
        })
      );
      const link = Object.assign(document.createElement('a'), {
        href: url,
        download: 'sync-capture.ndjson',
      });
      link.click();
      URL.revokeObjectURL(url);
      return lines.length;
    },
    replay: (lines: NdjsonBootstrapLine[]): Promise<void> =>
      sdk().replayLines(lines),
    diff: async (table: DiffTable) => {
      if (!DIFF_TABLES.includes(table)) {
        throw new Error(
          `__sync.diff: table must be one of ${DIFF_TABLES.join(', ')}`
        );
      }
      const server = await serverIds(sdk(), table);
      const local = new Set(
        (await rows(`SELECT id FROM ${table} WHERE deleted_at IS NULL`)).map(
          ([id]) => String(id)
        )
      );
      return {
        table,
        server: server.size,
        local: local.size,
        missingLocally: [...server]
          .filter((id) => !local.has(id))
          .slice(0, DIFF_ID_CAP),
        extraLocally: [...local]
          .filter((id) => !server.has(id))
          .slice(0, DIFF_ID_CAP),
      };
    },
    verbose: (on: boolean): void => {
      if (on) {
        deps.logger.level = LogLevel.Debug;
        if (!deps.consoleAttached)
          detachConsole ??= deps.logger.addTransport(consoleTransport);
        storage?.setItem(SYNC_VERBOSE_KEY, '1');
      } else {
        deps.logger.level = originalLevel;
        detachConsole?.();
        detachConsole = undefined;
        storage?.removeItem(SYNC_VERBOSE_KEY);
      }
    },
    isVerbosePersisted: (): boolean =>
      storage?.getItem(SYNC_VERBOSE_KEY) === '1',
    help: (): void => console.info(HELP),
  };
}

function safeLocalStorage(): SyncConsoleDeps['storage'] | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
